<template>
  <v-card id="notify" class="cnc-card mb-4" outlined>
    <!-- ส่วนหัว + ตัวเลขสรุป (คลิกเพื่อกรอง) -->
    <div class="cnc-header pa-4">
      <div class="d-flex flex-wrap align-center">
        <v-icon color="#b3151b" class="mr-2">mdi-bell-badge-outline</v-icon>
        <h3 class="cnc-title mr-3">ศูนย์แจ้งเตือนภารกิจสถานศึกษา</h3>
        <v-spacer />
        <span v-if="data.today" class="caption grey--text mr-2">ข้อมูล ณ {{ thaiDate(data.today) }}</span>
        <v-btn icon small :loading="loading" title="โหลดใหม่" @click="load">
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
      </div>

      <v-row dense class="mt-3">
        <v-col v-for="k in kpis" :key="k.key" cols="6" md="3">
          <div
            class="cnc-kpi"
            :class="{ 'cnc-kpi--active': tab === k.tab && (k.urgentOnly === urgentOnly) }"
            :style="{ borderLeftColor: k.hex }"
            role="button"
            @click="pickKpi(k)"
          >
            <div class="cnc-kpi-value" :style="{ color: k.hex }">{{ k.value }}</div>
            <div class="cnc-kpi-label">{{ k.label }}</div>
          </div>
        </v-col>
      </v-row>

      <!-- กรองตามกลุ่มงาน -->
      <div class="mt-3 cnc-groups">
        <v-chip
          small
          :outlined="groupFilter !== null"
          color="#263238"
          :dark="groupFilter === null"
          class="mr-1 mb-1"
          @click="groupFilter = null"
        >
          ทุกกลุ่มงาน
        </v-chip>
        <v-chip
          v-for="g in groupChips"
          :key="g.key"
          small
          :outlined="groupFilter !== g.key"
          :dark="groupFilter === g.key"
          color="#263238"
          class="mr-1 mb-1"
          @click="groupFilter = groupFilter === g.key ? null : g.key"
        >
          <v-icon left small>{{ g.icon }}</v-icon>{{ g.title }}
          <v-avatar v-if="g.pending" right color="red darken-1" class="white--text cnc-badge">{{ g.pending }}</v-avatar>
        </v-chip>
      </div>
    </div>

    <v-alert v-if="error" type="warning" text dense class="ma-4 mb-0">{{ error }}</v-alert>

    <v-tabs v-model="tab" height="42" class="cnc-tabs" show-arrows>
      <v-tab v-for="t in tabs" :key="t.key" class="cnc-tab">
        <v-icon left small>{{ t.icon }}</v-icon>{{ t.label }}
        <v-chip x-small class="ml-2" :color="t.color" dark>{{ t.count }}</v-chip>
      </v-tab>
    </v-tabs>
    <v-divider />

    <div class="pa-3">
      <v-skeleton-loader v-if="loading && !loaded" type="list-item-three-line@3" />

      <div v-else-if="!visible.length" class="cnc-empty">
        <v-icon color="success" class="mr-1">mdi-check-decagram</v-icon>
        {{ emptyText }}
      </div>

      <div v-for="t in visible" :key="t.key" class="cnc-item" :class="'cnc-item--' + displayStatus(t)">
        <div class="d-flex align-start">
          <v-icon class="mr-3 mt-1" :color="statusMeta(t).color">{{ statusMeta(t).icon }}</v-icon>

          <div class="flex-grow-1 cnc-item-body">
            <div class="d-flex flex-wrap align-center">
              <span class="cnc-item-title mr-2">{{ t.title }}</span>
              <v-chip v-if="t.urgent" x-small color="red darken-1" dark class="mr-1">
                <v-icon left x-small>mdi-fire</v-icon>เร่งด่วน
              </v-chip>
              <v-chip x-small outlined color="blue-grey" class="mr-1">
                <v-icon left x-small>{{ groupOf(t).icon }}</v-icon>{{ groupOf(t).title }}
              </v-chip>
            </div>

            <div class="cnc-item-meta">
              <template v-if="t.period">
                <v-icon x-small>mdi-calendar-range</v-icon>
                รอบ {{ t.period.times }}/{{ t.period.year }} · {{ thaiDate(t.period.start) }} – {{ thaiDate(t.period.stop) }}
                <v-chip x-small label :color="periodChip(t).color" dark class="ml-1">{{ periodChip(t).text }}</v-chip>
              </template>
              <template v-else>
                <v-icon x-small>mdi-infinity</v-icon> ไม่มีกำหนดการ — แจ้งเตือนจนกว่าจะดำเนินการครบ
              </template>
            </div>

            <div class="cnc-item-note">
              <v-icon x-small :color="statusMeta(t).color">mdi-information-outline</v-icon>
              {{ t.note }}
              <span v-if="t.acked_at" class="grey--text">
                · ยืนยันเมื่อ {{ thaiDateTime(t.acked_at) }}<span v-if="t.ack_note"> ({{ t.ack_note }})</span>
              </span>
            </div>

            <v-progress-linear
              v-if="t.progress.total > 1 && t.period_state !== 'upcoming'"
              :value="(t.progress.done / t.progress.total) * 100"
              :color="statusMeta(t).color"
              height="6"
              rounded
              class="mt-2 cnc-progress"
            />
          </div>

          <v-chip small :color="statusMeta(t).color" dark class="ml-2 flex-shrink-0 hidden-xs-only">
            {{ statusText(t) }}
          </v-chip>
        </div>

        <div class="cnc-actions">
          <v-btn
            v-if="t.period_state !== 'upcoming'"
            small
            depressed
            :color="isPending(t) ? '#b3151b' : 'blue-grey lighten-5'"
            :dark="isPending(t)"
            :to="t.route"
          >
            <v-icon left small>{{ isPending(t) ? "mdi-arrow-right-bold-circle" : "mdi-eye" }}</v-icon>
            {{ isPending(t) ? "ดำเนินการ" : "ดูรายการ" }}
          </v-btn>
          <v-btn small text color="primary" @click="openGuide(t.key)">
            <v-icon left small>mdi-format-list-numbered</v-icon>ขั้นตอนทำรายการ
          </v-btn>
          <template v-if="t.check === 'manual' && t.period_state !== 'upcoming' && t.status !== 'missed'">
            <v-btn
              v-if="t.status !== 'done'"
              small
              outlined
              color="success"
              :disabled="!data.can_ack"
              :loading="acking === t.key"
              :title="data.can_ack ? '' : ackDisabledReason"
              @click="ack(t)"
            >
              <v-icon left small>mdi-check-bold</v-icon>ยืนยันว่าทำรายการแล้ว
            </v-btn>
            <v-btn
              v-else
              small
              text
              color="grey darken-1"
              :disabled="!data.can_ack"
              :loading="acking === t.key"
              @click="unack(t)"
            >
              <v-icon left small>mdi-undo</v-icon>ยกเลิกการยืนยัน
            </v-btn>
          </template>
          <span v-if="t.check === 'auto' && isPending(t)" class="caption grey--text ml-1">
            <v-icon x-small>mdi-robot-outline</v-icon> สถานะจะเปลี่ยนเองเมื่อทำรายการครบในหน้าจัดการ
          </span>
        </div>
      </div>
    </div>

    <CollegeTaskGuide v-model="guideOpen" :task-key="guideKey" />
  </v-card>
</template>

<script>
// ศูนย์แจ้งเตือนภารกิจสถานศึกษา — ข้อมูลจาก college_notification_center.php (backend ตรวจสถานะจากข้อมูลจริงแล้ว)
// แทน components/college/alert.vue เดิมที่ยิง API ~20 ครั้งและดูแค่ว่ากำหนดการเปิดอยู่หรือไม่
import { GROUPS, GROUP_ORDER, STATUS_META } from "./collegeTaskCatalog";
import CollegeTaskGuide from "./CollegeTaskGuide.vue";

const PERIOD_CHIP = {
  open: { color: "green darken-1", text: "เปิดอยู่" },
  closing_soon: { color: "orange darken-2", text: "ใกล้ปิด" },
  overdue_open: { color: "blue-grey", text: "เลยวันที่กำหนด (ระบบยังเปิด)" },
  upcoming: { color: "indigo lighten-1", text: "กำลังจะเปิด" },
  closed: { color: "grey", text: "ปิดแล้ว" }
};

export default {
  name: "CollegeNotificationCenter",

  components: { CollegeTaskGuide },

  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      loaded: false,
      error: "",
      tab: 0,
      urgentOnly: false,
      groupFilter: null,
      acking: null,
      guideOpen: false,
      guideKey: null,
      data: { summary: {}, tasks: [], can_ack: false, ack_available: false }
    };
  },

  computed: {
    ackDisabledReason() {
      return this.data.ack_available
        ? "ผู้ดูแลระบบดูได้อย่างเดียว"
        : "ยังไม่ได้ติดตั้งตารางบันทึกการยืนยัน กรุณาแจ้งผู้ดูแลระบบ";
    },
    pendingTasks() {
      return this.data.tasks.filter(t => this.isPending(t));
    },
    upcomingTasks() {
      return this.data.tasks.filter(t => t.period_state === "upcoming");
    },
    finishedTasks() {
      return this.data.tasks.filter(t => !this.isPending(t) && t.period_state !== "upcoming");
    },
    tabs() {
      return [
        { key: "pending", label: "ต้องดำเนินการ", icon: "mdi-clipboard-alert-outline", color: "red darken-1", count: this.pendingTasks.length },
        { key: "upcoming", label: "กำลังจะเปิด", icon: "mdi-calendar-clock", color: "indigo lighten-1", count: this.upcomingTasks.length },
        { key: "finished", label: "ทำรายการแล้ว / ปิดแล้ว", icon: "mdi-check-all", color: "success", count: this.finishedTasks.length }
      ];
    },
    kpis() {
      const s = this.data.summary || {};
      return [
        { key: "todo", tab: 0, urgentOnly: false, label: "ต้องดำเนินการ", value: s.todo || 0, hex: "#c62828" },
        { key: "urgent", tab: 0, urgentOnly: true, label: "เร่งด่วน / ใกล้ปิด", value: s.urgent || 0, hex: "#ef6c00" },
        { key: "done", tab: 2, urgentOnly: false, label: "ทำรายการแล้ว", value: s.done || 0, hex: "#2e7d32" },
        { key: "upcoming", tab: 1, urgentOnly: false, label: "กำลังจะเปิด (30 วัน)", value: s.upcoming || 0, hex: "#3949ab" }
      ];
    },
    groupChips() {
      return GROUP_ORDER.filter(k => this.data.tasks.some(t => t.group === k)).map(k => ({
        key: k,
        title: GROUPS[k].title,
        icon: GROUPS[k].icon,
        pending: this.pendingTasks.filter(t => t.group === k).length
      }));
    },
    visible() {
      let list = [this.pendingTasks, this.upcomingTasks, this.finishedTasks][this.tab] || [];
      if (this.tab === 0 && this.urgentOnly) list = list.filter(t => t.urgent);
      if (this.groupFilter) list = list.filter(t => t.group === this.groupFilter);
      return list;
    },
    emptyText() {
      if (this.tab === 0) return this.groupFilter ? "กลุ่มงานนี้ไม่มีภารกิจค้าง" : "ไม่มีภารกิจค้าง — ทำรายการครบทุกภารกิจที่เปิดอยู่แล้ว";
      if (this.tab === 1) return "ยังไม่มีกำหนดการที่จะเปิดภายใน 30 วัน";
      return "ยังไม่มีภารกิจที่ทำรายการแล้วในช่วงนี้";
    }
  },

  watch: {
    tab() {
      this.urgentOnly = false;
    }
  },

  mounted() {
    this.load();
  },

  methods: {
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const res = await this.$http.post("college_notification_center.php", { ApiKey: this.ApiKey });
        if (res.data && res.data.status) {
          this.data = res.data;
          this.loaded = true;
          this.$emit("loaded", res.data.tasks);
          // ให้กระดิ่งใน AppBar อัปเดตทันที (เช่น หลังกดยืนยันว่าทำรายการแล้ว)
          this.$root.$emit("college-notify-summary", res.data.summary);
        } else {
          this.error = (res.data && res.data.message) || "ไม่สามารถโหลดการแจ้งเตือนได้";
        }
      } catch (e) {
        // 401 ถูกจัดการโดย interceptor ใน main.js แล้ว (แจ้ง session หมดอายุ)
        this.error = "ไม่สามารถโหลดการแจ้งเตือนได้ กรุณาลองใหม่อีกครั้ง";
      } finally {
        this.loading = false;
      }
    },

    async ack(t) {
      const res = await Swal.fire({
        title: "ยืนยันว่าทำรายการแล้ว",
        html: `<div style="text-align:left">${this.escape(t.title)}<br><small>รอบ ${t.period.times}/${t.period.year}</small></div>`,
        input: "text",
        inputPlaceholder: "หมายเหตุ (ถ้ามี) เช่น เลขที่หนังสือนำส่ง",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก",
        confirmButtonColor: "#2e7d32"
      });
      if (!res.isConfirmed) return;
      await this.sendAck(t, "ack", res.value || "");
    },

    async unack(t) {
      const res = await Swal.fire({
        title: "ยกเลิกการยืนยัน?",
        text: t.title,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "ยกเลิกการยืนยัน",
        cancelButtonText: "ปิด"
      });
      if (!res.isConfirmed) return;
      await this.sendAck(t, "unack", "");
    },

    async sendAck(t, action, note) {
      this.acking = t.key;
      try {
        const res = await this.$http.post("college_notification_center.php", {
          ApiKey: this.ApiKey,
          action,
          task_key: t.key,
          round_key: t.round_key,
          note
        });
        if (res.data && res.data.status) {
          await this.load();
        } else {
          Swal.fire({ icon: "warning", title: (res.data && res.data.message) || "ดำเนินการไม่สำเร็จ" });
        }
      } catch (e) {
        Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองใหม่" });
      } finally {
        this.acking = null;
      }
    },

    pickKpi(k) {
      this.tab = k.tab;
      // watcher ของ tab รีเซ็ต urgentOnly — ตั้งค่าหลัง watcher ทำงาน
      this.$nextTick(() => {
        this.urgentOnly = k.urgentOnly;
      });
    },

    openGuide(key) {
      this.guideKey = key;
      this.guideOpen = true;
    },

    isPending(t) {
      return (t.status === "todo" || t.status === "partial") && t.period_state !== "upcoming";
    },
    displayStatus(t) {
      return t.period_state === "upcoming" ? "upcoming" : t.status;
    },
    statusMeta(t) {
      return STATUS_META[this.displayStatus(t)] || STATUS_META.todo;
    },
    statusText(t) {
      const base = this.statusMeta(t).text;
      if (t.status === "partial") return `${base} ${t.progress.done}/${t.progress.total}`;
      return base;
    },
    groupOf(t) {
      return GROUPS[t.group] || { title: t.group, icon: "mdi-folder" };
    },
    periodChip(t) {
      const meta = PERIOD_CHIP[t.period_state] || PERIOD_CHIP.open;
      if ((t.period_state === "open" || t.period_state === "closing_soon") && t.days_to_stop !== null) {
        return { color: meta.color, text: t.days_to_stop === 0 ? "ปิดวันนี้" : `${meta.text} · เหลือ ${t.days_to_stop} วัน` };
      }
      if (t.period_state === "upcoming" && t.days_to_start !== null) {
        return { color: meta.color, text: `เปิดในอีก ${t.days_to_start} วัน` };
      }
      return meta;
    },
    thaiDate(raw) {
      if (!raw) return "-";
      const d = new Date(String(raw).substring(0, 10) + "T00:00:00");
      if (isNaN(d)) return raw;
      return d.toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "2-digit" });
    },
    thaiDateTime(raw) {
      const d = new Date(String(raw).replace(" ", "T"));
      if (isNaN(d)) return raw;
      return d.toLocaleString("th-TH", { day: "numeric", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });
    },
    escape(s) {
      return String(s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    }
  }
};
</script>

<style scoped>
.cnc-card {
  border-radius: 14px !important;
  overflow: hidden;
  border: 1px solid #e4e4e7 !important;
}
.cnc-header {
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  border-bottom: 1px solid #e2e8f0;
  border-left: 3px solid #b3151b;
}
.cnc-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1b365d;
}
.cnc-kpi {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-left: 4px solid;
  border-radius: 10px;
  padding: 10px 12px;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.cnc-kpi:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}
.cnc-kpi--active {
  box-shadow: 0 0 0 2px rgba(27, 54, 93, 0.35);
}
.cnc-kpi-value {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.2;
}
.cnc-kpi-label {
  font-size: 0.8rem;
  color: #616161;
}
.cnc-badge {
  font-size: 0.7rem !important;
}
.cnc-tab {
  text-transform: none;
  font-size: 0.88rem;
}
.cnc-empty {
  color: #616161;
  font-size: 0.92rem;
  padding: 18px 4px;
}
.cnc-item {
  border: 1px solid #e2e8f0;
  border-left: 4px solid #cfd8dc;
  border-radius: 10px;
  padding: 12px 14px 8px;
  margin-bottom: 10px;
  background: #fff;
}
.cnc-item--todo {
  border-left-color: #e53935;
}
.cnc-item--partial {
  border-left-color: #ffa000;
}
.cnc-item--done,
.cnc-item--none {
  border-left-color: #43a047;
  background: #fbfefb;
}
.cnc-item--missed {
  border-left-color: #9e9e9e;
  background: #fafafa;
}
.cnc-item--upcoming {
  border-left-color: #5c6bc0;
}
.cnc-item-body {
  min-width: 0;
}
.cnc-item-title {
  font-weight: 600;
  color: #263238;
}
.cnc-item-meta,
.cnc-item-note {
  font-size: 0.82rem;
  color: #607d8b;
  margin-top: 4px;
}
.cnc-item-note {
  color: #455a64;
}
.cnc-progress {
  max-width: 420px;
}
.cnc-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding-left: 36px;
}
@media (max-width: 599px) {
  .cnc-actions {
    padding-left: 0;
  }
}
</style>
